import { fmtDE } from './dateUtils.js';

export const ValueMixin = {
    setOptions(options = [], { sort = true } = {}) {
        const groups = Array.isArray(options[0]) ? options : [options];
        const sortedGroups = sort
            ? groups.map(group => [...group].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' })))
            : groups;

        this.options = [];
        sortedGroups.forEach((group, i) => {
            if (i > 0) this.options.push({ divider: true });
            this.options.push(...group);
        });

        if ((this.getAttribute('type') || 'dropdown') === 'dropdown') {
            const multiple = this.hasAttribute('multiple');
            if (this.panel) this.buildOptions(this.panel, multiple);
            this.updateTriggerLabel(multiple);
        }
    },

    setValue(value, { silent = false } = {}) {
        if (!this.trigger && !this.textInput) {
            this._pendingSetValue = { value, silent };
            return;
        }
        const hasValue = Array.isArray(value)
            ? value.length > 0
            : (value !== null && value !== undefined && value !== '');
        if (hasValue) this.removeAttribute('error');
        const type    = this.getAttribute('type') || 'dropdown';
        const prevVal = JSON.stringify(this.getValue());

        if (type === 'daterange' && value && typeof value === 'object') {
            this.rangeStart = value.from ?? null;
            this.rangeEnd   = value.to   ?? null;
            this.updateRangeTrigger();
            if (this.panel) this.buildRangePanel();

        } else if (type === 'text' || type === 'textarea') {
            this.value = value ?? '';
            if (this.textInput) this.textInput.value = this.value;
            if (type === 'textarea') this.autoGrowTextarea?.();

        } else if (type === 'timepicker') {
            this.timeValue = (typeof value === 'string' && value) ? value : null;
            this.updateTimepickerTrigger?.();
            if (this.panel) this.buildTimepickerPanel?.();

        } else if (type === 'timerange' && value && typeof value === 'object') {
            this.timeFrom = value.from ?? this.timeFrom;
            this.timeTo   = value.to   ?? this.timeTo;
            this.updateTimerangeTrigger();
            if (this.panel) this.buildTimerangePanel();

        } else if (type === 'datepicker') {
            this.value = value;
            if (value) {
                const [y, m] = value.split('-').map(Number);
                this.calViewDate = new Date(y, m - 1, 1);
            }
            if (this.dateInput) {
                this.dateInput.value = value ? fmtDE(value) : '';
                if (!value) this.dateInput.placeholder = this.getAttribute('placeholder') || 'TT.MM.JJJJ';
            }
            if (this.panel) this.buildCalendar();

        } else {
            this.value = value;
            const multiple = this.hasAttribute('multiple');
            this.updateTriggerLabel(multiple);
            if (this.panel) this.buildOptions(this.panel, multiple);
        }

        if (!silent && JSON.stringify(this.getValue()) !== prevVal) {
            const detail = type === 'daterange'
                ? { from: this.rangeStart, to: this.rangeEnd }
                : type === 'timerange'
                ? { from: this.timeFrom,   to: this.timeTo   }
                : type === 'timepicker'
                ? { value: this.timeValue }
                : { value: this.value };
            this.dispatchEvent(new CustomEvent('input-change', { bubbles: true, composed: true, detail }));
        }
    },

    setError() {
        // Removing + reflowing before re-adding restarts the CSS flash animation even
        // when the field is already marked as an error (e.g. clicking Save again).
        this.removeAttribute('error');
        void this.offsetWidth;
        this.setAttribute('error', '');
        this.addEventListener('input-change', () => { this.removeAttribute('error'); }, { once: true });
    },

    getValue() {
        const type = this.getAttribute('type') || 'dropdown';
        if (type === 'daterange')  return { from: this.rangeStart, to: this.rangeEnd };
        if (type === 'timerange')  return { from: this.timeFrom,   to: this.timeTo   };
        if (type === 'timepicker') return this.timeValue ?? '';
        return this.value;
    },

    getLabel() {
        const type = this.getAttribute('type') || 'dropdown';
        if (type === 'daterange') {
            return (this.rangeStart && this.rangeEnd)
                ? `${fmtDE(this.rangeStart)} – ${fmtDE(this.rangeEnd)}`
                : null;
        }
        if (type === 'datepicker') return this.value ? fmtDE(this.value) : null;
        if (type === 'timerange')  return `${this.timeFrom} – ${this.timeTo}`;
        if (type === 'timepicker') return this.timeValue;
        if (Array.isArray(this.value)) {
            return this.value
                .map(v => this.options.find(o => String(o.id) === String(v))?.name)
                .filter(Boolean);
        }
        return this.options.find(o => String(o.id) === String(this.value))?.name ?? null;
    },

    setEmptyOption(label = null) {
        if (label !== null) {
            this.setAttribute('empty-option', '');
            this.setAttribute('empty-label', label);
        } else {
            this.removeAttribute('empty-option');
            this.removeAttribute('empty-label');
        }
    },
};
